import { useGetScene, useListSceneActivations } from '@smarthome/api';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, Clock, History } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';

export function SceneActivationHistoryPage() {
  const { id } = useParams<{ id: string }>();

  const { data: scene, isLoading: sceneLoading } = useGetScene(id!, {
    query: { enabled: !!id },
  });

  const { data: activationsData, isLoading: activationsLoading } = useListSceneActivations(
    id!,
    { limit: 100 },
    {
      query: {
        enabled: !!id,
        refetchInterval: 30000,
      },
    }
  );

  const activations = activationsData?.activations ?? [];
  const isLoading = sceneLoading || activationsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to={`/scenes/${id}`}
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Activation History
          </h1>
          <p className="text-sm text-gray-500 dark:text-zinc-500">{scene?.name ?? 'Scene'}</p>
        </div>
      </div>

      {/* Main content card */}
      <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        {/* Helper note */}
        <div className="border-b border-gray-200 px-4 py-3 dark:border-white/[0.06]">
          <p className="text-xs text-gray-500 dark:text-zinc-500">
            Some activations may not change devices if targets already match the desired state.
          </p>
        </div>

        {activations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="mb-3 rounded-full bg-gray-100 p-3 dark:bg-zinc-800">
              <History className="h-6 w-6 text-gray-400 dark:text-zinc-600" />
            </div>
            <p className="text-sm text-gray-500 dark:text-zinc-500">No activation history yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
            {activations.map((activation) => (
              <ActivationEntry key={activation.id} activation={activation} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ActivationEntryProps {
  activation: {
    id?: string;
    status?: string;
    triggerType?: string;
    triggeredAt?: string;
    triggerContext?: Record<string, unknown>;
    summary?: { devicesSet?: number; devicesFailed?: number };
  };
}

function ActivationEntry({ activation }: ActivationEntryProps) {
  const [showDetails, setShowDetails] = useState(false);

  const statusConfig = useMemo(() => {
    switch (activation.status) {
      case 'completed':
        return {
          icon: CheckCircle,
          color: 'text-green-500',
          bg: 'bg-green-500/10',
          label: 'Completed',
        };
      case 'failed':
        return { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'Failed' };
      case 'partial':
        return {
          icon: AlertTriangle,
          color: 'text-amber-500',
          bg: 'bg-amber-500/10',
          label: 'Partial',
        };
      default:
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-400/10', label: 'Unknown' };
    }
  }, [activation.status]);

  const StatusIcon = statusConfig.icon;

  const formattedTime = useMemo(() => {
    if (!activation.triggeredAt) return 'Unknown';
    const date = new Date(activation.triggeredAt);
    return date.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  }, [activation.triggeredAt]);

  const devicesSummary = useMemo(() => {
    const summary = activation.summary;
    if (!summary) return null;

    const devicesSet = summary.devicesSet ?? 0;
    const devicesFailed = summary.devicesFailed ?? 0;

    if (devicesSet === 0 && devicesFailed === 0) {
      return 'No device changes';
    }

    const parts: string[] = [];
    if (devicesSet > 0) {
      parts.push(`${devicesSet} device${devicesSet === 1 ? '' : 's'} affected`);
    }
    if (devicesFailed > 0) {
      parts.push(`${devicesFailed} failed`);
    }

    return parts.join(', ');
  }, [activation.summary]);

  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className={`mt-0.5 rounded-lg p-2 ${statusConfig.bg}`}>
          <StatusIcon className={`h-4 w-4 ${statusConfig.color}`} />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {statusConfig.label}
            </span>
            {activation.triggerType && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
                {activation.triggerType}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-zinc-500">
            <span>{formattedTime}</span>
            {devicesSummary && (
              <>
                <span className="text-gray-300 dark:text-zinc-700">·</span>
                <span>{devicesSummary}</span>
              </>
            )}
          </div>
        </div>

        {/* Details toggle */}
        {activation.triggerContext && Object.keys(activation.triggerContext).length > 0 && (
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10"
          >
            {showDetails ? 'Hide details' : 'Details'}
          </button>
        )}
      </div>

      {/* Details panel */}
      {showDetails && activation.triggerContext && (
        <div className="ml-11 mt-3 overflow-hidden rounded-lg bg-gray-50 dark:bg-zinc-800/50">
          <div className="border-b border-gray-200 px-3 py-2 dark:border-white/[0.06]">
            <span className="text-xs font-medium text-gray-500 dark:text-zinc-500">
              Trigger Context
            </span>
          </div>
          <pre className="overflow-x-auto p-3 font-mono text-xs text-gray-600 dark:text-zinc-400">
            {JSON.stringify(activation.triggerContext, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
